import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { authzContextAtom } from '../../../../authorization/authzState';
import { userInformationAtom } from '../../../../Services/_states';
import { ConstantActions } from '../../../../Services/_actions/index.js';
import {
  canAddComment,
  canBulkUpdateRecords,
  canCreateRecord,
  canDeleteRecord,
  canDownloadRecords,
  canEditRecordForm,
  canEditRecord,
  canEnterRecords,
  canImportRecords,
  canManageRecordAsAdmin,
  canMoveRecords,
  getDownloadPermissionKey,
  canOpenWebForm,
  canQuickCallRecord,
  canUseRecordFilters,
  canViewFormDetail,
  canViewHelp,
  canViewImages,
  canViewReports,
  canViewStatusLogs,
  canSendRecordTemplate,
  canAssignLead,
  canUseLeadChat,
} from '../utils/canManageRecords';

export default function useRecordPermissions(explicitWorkspaceId, explicitAccountId) {
  const { workspaceId: routeWorkspaceId, accountId: routeAccountId } = useParams();
  const authz = useRecoilValue(authzContextAtom);
  const userInfo = useRecoilValue(userInformationAtom);
  const { userHasPermission } = ConstantActions();

  const workspaceId = explicitWorkspaceId || routeWorkspaceId;
  const accountId = explicitAccountId || routeAccountId;

  const baseParams = useMemo(
    () => ({
      workspaceId,
      accountId,
      userInfo,
      legacyUserHasPermission: userHasPermission,
    }),
    [workspaceId, accountId, userInfo, userHasPermission]
  );

  const canDownload = canDownloadRecords(authz, baseParams);
  const downloadPermissionKey = getDownloadPermissionKey(authz, baseParams);

  useEffect(() => {
    if (!workspaceId) return;
    console.info('[Record permissions] Download records', {
      permissionKey: downloadPermissionKey,
      visible: canDownload,
    });
  }, [workspaceId, downloadPermissionKey, canDownload]);

  return useMemo(
    () => ({
      canCreate: canCreateRecord(authz, baseParams),
      canEdit: canEditRecord(authz, baseParams),
      canDelete: canDeleteRecord(authz, baseParams),
      canBulkUpdate: canBulkUpdateRecords(authz, baseParams),
      canMoveRecords: canMoveRecords(authz, baseParams),
      canFilter: canUseRecordFilters(authz, baseParams),
      canDownload,
      canImport: canImportRecords(authz, baseParams),
      canEnterRecords: canEnterRecords(authz, baseParams),
      canEditForm: canEditRecordForm(authz, baseParams),
      canViewReports: canViewReports(authz, baseParams),
      canViewHelp: canViewHelp(authz, baseParams),
      canAddComment: canAddComment(authz, baseParams),
      canQuickCall: canQuickCallRecord(authz, baseParams),
      canOpenWebForm: canOpenWebForm(authz, baseParams),
      canManageRecord: canManageRecordAsAdmin(authz, baseParams),
      canViewFormDetail: canViewFormDetail(authz, baseParams),
      canViewStatusLogs: canViewStatusLogs(authz, baseParams),
      canViewImages: canViewImages(authz, baseParams),
      canSendTemplate: canSendRecordTemplate(authz, baseParams),
      canAssignLead: canAssignLead(authz, baseParams),
      canUseLeadChat: canUseLeadChat(authz, baseParams),
    }),
    [authz, baseParams, canDownload]
  );
}
